


































import {
  Component,
  Vue,
  Prop,
  Emit,
  PropSync,
  Watch,
} from "vue-property-decorator";
import * as farmsService from "@/services/farms";
import { farmDTO as farmDTOFactory} from "@/api/farms/types";
import FarmDrawingModal from "./FarmDrawingModal.vue";
import FarmDrawConfirm from "../farm-draw-confirm/FarmDrawConfirm.vue";
import Geometry from "ol/geom/Geometry";
import { getAreaInHectares, getCentroid, intersectsItself } from "@/helpers/geometry";
import ConfirmationModal from "@/components/confirmationModal/ConfirmationModal.vue";
import * as locationService from "@/services/location";
import * as notificationService from "@/services/notification";
import { CityRequest } from "@/api/location/types";
import { RuralInfo } from "@/api/car/types";
import CarFindModal from "../car-find-modal/CarFindModal.vue";
import { FarmProperties } from "../farm-draw-confirm/types";

@Component({
  name: "FarmController",
  components: {
    FarmDrawingModal,
    FarmDrawConfirm,
    ConfirmationModal,
    CarFindModal,
  },
})
export default class FarmController extends Vue {
  @PropSync("state", { type: String, default: "closed" })
  private stateSynced!: string;

  private farmGeometry: Geometry | null = null;
  private farmArea: number | null = null;
  private farmName: string | null = null;
  private farmCarCode: string = '';
  private isCarFindModalVisible: boolean = false;
  private carResponse: RuralInfo | null = null;

  private get farmProps(): FarmProperties {
    return {
      farmName: this.farmName!,
      ownerName: this.owner?.name ?? "",
      cityName: this.cityFromCentroid?.cityName ?? "",
      stateAcronym: this.cityFromCentroid?.stateAcronym ?? "",
      area: this.formattedAreaInHa,
    };
  }

  private get owner() {
    return this.$store.state.owner.ownerContext;
  }

  private get cityFromCentroid() {
    return this.$store.state.location.cityFromCentroid;
  }

  private get formattedAreaInHa() {
    if (!this.farmArea) {
      return "";
    }
    return `${this.$n(this.farmArea, "rounded-number")} ha`;
  }

  private get confirmationText() {
    return this.$t("farms.farm-controller.confirmation-label", {
      ownerName: this.owner?.name ?? "",
    });
  }

  private get isMapVisible() {
    return this.stateSynced !== "closed";
  }

  private get isMapDisabled() {
    return this.stateSynced !== "initial";
  }

  private get isFarmNameModalOpen() {
    return this.stateSynced === "draw:complete";
  }

  private get isConfirmationOpen() {
    return this.stateSynced === "farm-name:complete";
  }

  private get createFarmStatus() {
    return this.$store.state.farms.createStatus;
  }

  private async finishDrawing(farmGeometry: Geometry) {
    const isValid = !intersectsItself(farmGeometry);
    if (isValid) { 
      this.farmGeometry = farmGeometry;
      this.farmArea = getAreaInHectares(farmGeometry).value;
      await locationService.fetchCityByCentroid(
        CityRequest.withCentroid(getCentroid(farmGeometry))
      );
      this.stateSynced = "draw:complete";
    } else {
      (this.$refs.drawingModal as any).clearDrawing();
      notificationService.addErrorNotification(this.$t("notifications.error.invalid-geometry-intersection") as string);
    } 
  }

  @Watch("createFarmStatus", { immediate: true })
  private onChangeStatus(status: string) {
    if (status === "fetch:farm" && this.stateSynced === "creating:farm") {
      this.onCreateSuccess();
    }

    if (status === "error:farm" && this.stateSynced === "creating:farm") {
      this.goToFarmNameStep();
    }
  }

  @Emit("create:farm")
  private onCreateSuccess() {
    this.stateSynced = "closed";
    this.resetDrawing();
  }

  @Emit("cancel")
  private cancelCreation() {
    this.resetDrawing();
    this.stateSynced = "closed";
  }

  private goToDrawingStep() {
    this.resetDrawing();
    this.stateSynced = "initial";
  }

  private resetDrawing() {
    this.farmName = ''
    this.farmArea = null;
    this.farmGeometry = null;
    this.farmCarCode = '';
    this.carResponse = null;
    (this.$refs.drawingModal as any).clearDrawing();
  }

  private selectedFarmName(farmName: string) {
    this.farmName = farmName;
    this.stateSynced = "farm-name:complete";
  }

  private goToFarmNameStep() {
    this.stateSynced = "draw:complete";
  }

  private createFarm() {
    const farm = farmDTOFactory(
        this.farmName!,
        this.owner?.uuid ?? null,
        this.farmGeometry!,
        this.farmCarCode);
    
    farmsService.createFarm(farm);
    this.stateSynced = "creating:farm";
  }

  private showCarModal() {
    this.isCarFindModalVisible = true;
  }

  private cancelFindByCar() {
    this.isCarFindModalVisible = false;
  }

  private getFarmByCarCodeResponse(response: RuralInfo) {
    this.carResponse = response;
    this.farmCarCode = response.ruralCode ?? '';
    this.farmName = this.farmCarCode;
    this.isCarFindModalVisible = false;
  }
}
